// PortfolioRenderer — the themed portfolio body shared by the editor preview
// and the public page (EPD-10101). One renderer, both surfaces, so they cannot
// drift. The active theme is mapped onto shadcn CSS variables (re-skinning the
// reused public-profile components) plus `--pf-*` tokens, and a scoped style
// block binds the heading font and creator-set type scale.
//
// Empty states (EPD-10137 / EPD-10124): each block is driven by its own data.
// In the "editor" surface a block with no content shows a prompt; in the
// "public" surface the same block is omitted entirely.
//
// Responsive layout (EPD-11532): the root is a named query container
// (`@container/portfolio`), and the renderer and its blocks respond to its
// width rather than the viewport. The editor's mobile preview is a 390px div
// inside the desktop page, so viewport breakpoints (`sm:` etc.) never fire
// there. Use ONE breakpoint throughout: `@[30rem]/portfolio:` (480px of
// container width). Unprefixed classes are the phone layout; the prefixed ones
// are the wider layout.
//
// Desktop scale (EPD-12193): the wider layout also widens the content column
// and scales the theme's type sizes by PORTFOLIO_DESKTOP_TYPE_SCALE (the
// `--pf-scale` token), so desktop has less side white space and slightly
// larger text. The phone layout is unchanged.

import { formatPortfolioLocation } from "@repo/core";
import { PublicProfileHeader } from "@/components/public-profile";
import {
  isLight,
  orderedChannels,
  portfolioHeroCssVars,
  portfolioThemeToCssVars,
  visibleChannels,
  withMissingSections,
  type PortfolioSection,
  type PortfolioTheme,
} from "@/lib/portfolio/theme";
import { hasCollaborations } from "@/lib/portfolio/collaborations";
import type { SocialStatsEntry } from "@/lib/queries/channel";
import type {
  PublicHighlightPostStats,
  PublicPreferenceItem,
  PublicProfileData,
} from "@/lib/queries/public-profile";
import {
  InstagramIcon,
  PinterestIcon,
  TikTokIcon,
  YouTubeIcon,
} from "@/components/icons/social-icons";
import { ensureFontLoaded, fontSynthesisWeight } from "@/lib/portfolio/fonts";
import { anyDemographicsEnabled } from "@/lib/portfolio/metric-catalog";
import { cn } from "@/lib/utils";
import { toast } from "@repo/ui";
import {
  AtSign,
  CircleDollarSign,
  FileText,
  Film,
  ImageIcon,
  Mail,
  Pencil,
} from "lucide-react";
import { useEffect, useId, useMemo, useState, type ReactElement } from "react";
import { EmptyPill, EmptyPrompt } from "./portfolio-empty-state";
import { PortfolioPastCollaborations } from "./portfolio-past-collaborations";
import {
  PortfolioSocialAccounts,
  PortfolioSocialAccountsPlaceholder,
} from "./portfolio-social-accounts";

export type PortfolioSurface = "editor" | "public";

/** Type-size multiplier for the wider (desktop) layout (EPD-12193). */
export const PORTFOLIO_DESKTOP_TYPE_SCALE = 1.125;

// Content column: phone gutters, then a wider column with roomier gutters on
// desktop (EPD-12193). The header band's inner column and the body both use
// this one class (width AND gutters on the same box), so the header's text and
// photo line up with the sections' left and right edges (EPD-12294).
export const PORTFOLIO_CONTENT_COLUMN_CLASS =
  "mx-auto max-w-2xl px-6 @[30rem]/portfolio:max-w-5xl @[30rem]/portfolio:px-10";

/** One display row of the Rates block (EPD-10093, "List rates" mode). */
export interface PortfolioRate {
  id: string;
  label: string;
  price: string;
  note?: string;
  /** A creator-defined "Other" content rate (its label is free text). */
  custom: boolean;
}

const usd = (n: number) => `$${n.toLocaleString("en-US")}`;

/**
 * The prototype's rate string (`pfRateString`): `$850–$1,200` for a range,
 * whichever value is set when only one is (or both are equal), and nothing
 * when neither is. Thousands separators per EPD-10122.
 */
function rateString(
  low: number | null | undefined,
  high: number | null | undefined,
) {
  if (low != null && high != null && low !== high)
    return `${usd(low)}–${usd(high)}`;
  const one = low ?? high;
  return one == null ? "" : usd(one);
}

/**
 * The platform glyph shown in a rate row's chip, derived from the content type
 * or ad-access platform name (it isn't stored). Image / Video get a media
 * glyph; an "Other" rate's label is the creator's own text, so it always gets
 * the pencil, even when that text reads like a standard type (EPD-12031).
 */
export function RateGlyph({
  label,
  custom = false,
}: {
  label: string;
  custom?: boolean;
}) {
  const name = label.toLowerCase();
  const className = "h-3.5 w-3.5";
  if (custom) return <Pencil className={className} />;
  if (name.startsWith("instagram"))
    return <InstagramIcon className={className} />;
  if (name.startsWith("tiktok")) return <TikTokIcon className={className} />;
  if (name.startsWith("youtube")) return <YouTubeIcon className={className} />;
  if (name.startsWith("pinterest"))
    return <PinterestIcon className={className} />;
  if (name.startsWith("blog")) return <FileText className={className} />;
  if (name === "image") return <ImageIcon className={className} />;
  if (name === "video") return <Film className={className} />;
  return <Pencil className={className} />;
}

/**
 * Flatten the creator's stored rates into display rows: content rates
 * (`content_rates`, a low–high range per content type) first, then paid ad
 * access (`ad_access_rates`) for platforms the creator grants access on. A
 * "No" ad-access answer isn't a rate, so it's left out.
 */
export function portfolioRates(
  preferences: Record<string, PublicPreferenceItem[]>,
): PortfolioRate[] {
  const content = (preferences.content_rates ?? []).flatMap((item) => {
    const price = rateString(item.numericValue, item.numericValueHigh);
    if (!price) return [];
    const custom = item.key === "Other" || item.key.startsWith("Other: ");
    return [
      {
        id: item.id,
        // Custom types are stored two ways: the settings form's composite key
        // "Other: <label>", or the older key="Other" with the label in textValue.
        label:
          item.key === "Other" && item.textValue
            ? item.textValue
            : item.key.replace(/^Other: /, ""),
        price,
        custom,
      },
    ];
  });
  const adAccess = (preferences.ad_access_rates ?? []).flatMap((item) => {
    if (item.textValue !== "Yes" || item.numericValue == null) return [];
    return [
      {
        id: item.id,
        label: item.key,
        price: usd(item.numericValue),
        note:
          item.numericValueHigh === 1
            ? "30 days of ad access · negotiable"
            : "30 days of ad access",
        custom: false,
      },
    ];
  });
  return [...content, ...adAccess];
}

/** Deep links to the Profile editor for empty-state CTAs (editor surface only). */
export interface PortfolioEditHrefs {
  profile: string;
  channels: string;
}

interface PortfolioRendererProps {
  data: PublicProfileData;
  theme: PortfolioTheme;
  surface?: PortfolioSurface;
  editHrefs?: PortfolioEditHrefs;
  /** Per-platform social stats (EPD-10113). Omitted → the block is not shown. */
  socialStats?: SocialStatsEntry[];
  /**
   * True while `socialStats` is still loading: the Social Accounts block holds
   * its place with a placeholder so the sections after it don't jump down when
   * it arrives (EPD-12469).
   */
  socialStatsPending?: boolean;
  /** Read-through highlight post stats (EPD-11945), streamed in separately. */
  highlightPostStats?: PublicHighlightPostStats;
  /**
   * Fetches the contact email when a visitor clicks Contact (EPD-12502). The
   * live page's theme carries no address, so it passes this when the
   * portfolio has one; the editor and preview have the address in the theme.
   */
  loadContactEmail?: () => Promise<string>;
  className?: string;
}

export function PortfolioRenderer({
  data,
  theme,
  surface = "public",
  editHrefs,
  socialStats,
  socialStatsPending = false,
  highlightPostStats,
  loadContactEmail,
  className,
}: PortfolioRendererProps) {
  const rawId = useId();
  // useId() contains ":" which is invalid in a CSS class/selector — sanitize.
  const scopeClass = `pf-scope-${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const isEditor = surface === "editor";

  // Fetch the theme's fonts if the app doesn't load them up front, so the
  // public page and the preview render the face the creator picked.
  useEffect(() => {
    ensureFontLoaded(theme.fontHeading);
    ensureFontLoaded(theme.fontBody);
  }, [theme.fontHeading, theme.fontBody]);

  const showContent = theme.show.content !== false;
  const showStats = theme.show.stats !== false;
  // Audience demographics has no section toggle (EPD-12034): it shows whatever
  // demographic charts the Metrics Shown picker has enabled.
  const showAudience = anyDemographicsEnabled(theme.hiddenMetricIds);
  const showHighlights = theme.show.highlights !== false;
  const showRates = theme.show.rates !== false;
  const showContact = theme.show.contact !== false;

  // Accounts the creator chose not to feature (EPD-12194) are dropped from the
  // header icons and the Social Accounts block alike, and both follow the
  // order the creator dragged the accounts into (EPD-12481).
  const channels = useMemo(
    () =>
      orderedChannels(
        visibleChannels(data.channels, theme.hiddenChannelIds),
        theme.channelOrder,
      ),
    [data.channels, theme.hiddenChannelIds, theme.channelOrder],
  );
  const shownSocialStats = useMemo(
    () =>
      socialStats &&
      orderedChannels(
        visibleChannels(socialStats, theme.hiddenChannelIds),
        theme.channelOrder,
      ),
    [socialStats, theme.hiddenChannelIds, theme.channelOrder],
  );

  // The header's location line per the Location setting (EPD-12486). The
  // editor's draft carries the address parts, so the preview follows the
  // setting as it changes; the public payload is already trimmed by the API.
  const profile = useMemo(() => {
    const { locationParts } = data.profile;
    if (!locationParts) return data.profile;
    return {
      ...data.profile,
      location: formatPortfolioLocation(locationParts, theme.locationDisplay),
    };
  }, [data.profile, theme.locationDisplay]);

  // Connected-account recent posts double as thumbnails for collab links to
  // the same posts (EPD-12059).
  const recentPosts = useMemo(
    () => shownSocialStats?.flatMap((entry) => entry.stats?.posts ?? []),
    [shownSocialStats],
  );

  const noBio = !data.profile.description?.trim();
  // The connect prompt is about having no accounts at all, not about hiding them.
  const noSocial = data.channels.length === 0;
  const rates = portfolioRates(data.preferences);
  // Trim: a whitespace-only email must not render a dead Contact button.
  const contactEmail = theme.contactEmail.trim();
  const hasContact = contactEmail.length > 0 || loadContactEmail !== undefined;
  const [openingContact, setOpeningContact] = useState(false);

  // The address is never on the page (EPD-12502): the button holds no mailto
  // and the live page fetches the address only when clicked, then opens the
  // visitor's mail composer with it.
  async function openContactEmail() {
    let email = contactEmail;
    if (!email && loadContactEmail) {
      setOpeningContact(true);
      try {
        email = (await loadContactEmail()).trim();
      } catch {
        email = "";
      } finally {
        setOpeningContact(false);
      }
    }
    if (!email) {
      toast.error("We couldn’t load the email address. Please try again.");
      return;
    }
    window.location.href = `mailto:${email}`;
  }

  return (
    <div
      className={cn(scopeClass, "@container/portfolio min-h-full", className)}
      style={portfolioThemeToCssVars(theme)}
    >
      {/* Scoped typography: binds heading font + creator-set type sizes to the
          reused components without touching global styles. 400-only families
          turn off faux bold (headings incl. inline heading-font elements). */}
      {/* --pf-scale is set on the root's children, not the root: a container
          query can't style the container itself (EPD-12193). */}
      <style>{`
        .${scopeClass} h1 { font-family: var(--pf-font-heading); font-size: calc(var(--pf-h1) * var(--pf-scale, 1)); color: var(--pf-heading); line-height: 1.1; }
        .${scopeClass} h2 { font-family: var(--pf-font-heading); font-size: calc(var(--pf-h2) * var(--pf-scale, 1)); color: var(--pf-heading); }
        .${scopeClass} h3 { font-family: var(--pf-font-heading); font-size: calc(var(--pf-h3) * var(--pf-scale, 1)); color: var(--pf-heading); }
        .${scopeClass} { font-size: var(--pf-body); font-synthesis-weight: ${fontSynthesisWeight(theme.fontBody)}; }
        .${scopeClass} > * { font-size: calc(var(--pf-body) * var(--pf-scale, 1)); }
        @container portfolio (min-width: 30rem) { .${scopeClass} > * { --pf-scale: ${PORTFOLIO_DESKTOP_TYPE_SCALE}; } }
        .${scopeClass} :is(h1, h2, h3, [style*="--pf-font-heading"]) { font-synthesis-weight: ${fontSynthesisWeight(theme.fontHeading)}; }
      `}</style>

      {/* Header band — reflects the theme's "Header color" (heroSolid). Extra
          vertical padding makes it more prominent (EPD-12195). The colour runs
          full-bleed; the content sits in the body's column (EPD-12294). */}
      {/* Its text tokens are re-picked against the header colour so the
          name and bio stay readable on any theme (EPD-12471). */}
      <div
        style={portfolioHeroCssVars(theme)}
        className="pb-12 pt-14 @[30rem]/portfolio:pb-14 @[30rem]/portfolio:pt-16"
      >
        <div className={cn(PORTFOLIO_CONTENT_COLUMN_CLASS, "space-y-3")}>
          <PublicProfileHeader profile={profile} channels={channels} />

          {isEditor && noBio && (
            <EmptyPill
              icon={FileText}
              message="Add your bio in your profile."
              cta={
                editHrefs
                  ? { label: "Add bio", href: editHrefs.profile }
                  : undefined
              }
            />
          )}

          {isEditor && noSocial && (
            <EmptyPill
              icon={AtSign}
              message="Connect your social accounts."
              cta={
                editHrefs
                  ? { label: "Connect accounts", href: editHrefs.channels }
                  : undefined
              }
            />
          )}
        </div>
      </div>

      <div className={cn(PORTFOLIO_CONTENT_COLUMN_CLASS, "space-y-8 py-10")}>
        {(() => {
          const socialAccountsOn = showContent || showStats || showAudience;
          // Body sections in the creator's order, first occurrence of each.
          // withMissingSections keeps a stored order that predates a section
          // (e.g. "rates") rendering it.
          const sections = [
            ...new Set(withMissingSections(theme.sectionOrder)),
          ];
          // Past collaborations (EPD-10120) under the stored `highlights` id,
          // so saved section order/visibility carry over. Built from brand
          // collaboration highlights only (EPD-11127): the legacy preference
          // taxonomy (content categories, interests, …) is intentionally NOT
          // shown; it isn't part of the Creator Toolkit prototype.
          // On the public page it is left out when there is nothing to show
          // (the block would render nothing), so it isn't counted as shown.
          const highlightsBlock =
            showHighlights &&
            (isEditor || hasCollaborations(data.brandHighlights)) ? (
              <PortfolioPastCollaborations
                key="highlights"
                highlights={data.brandHighlights}
                postStats={highlightPostStats}
                recentPosts={recentPosts}
                surface={surface}
                background={isLight(theme.bg) ? "light" : "dark"}
                editHref={editHrefs?.profile}
              />
            ) : null;

          // Matches the prototype's ServicesSection (list mode): an h2 above a
          // bordered, hairline-separated list on the page background, each row
          // a platform chip + deliverable + rate. The heading takes the
          // Headline font; row labels and prices take the Body font (EPD-12061).
          const rateText = {
            fontFamily: "var(--pf-font-body)",
            fontSize: "calc(14.5px * var(--pf-scale, 1))",
            fontWeight: 500,
            color: "var(--pf-heading)",
          } as const;
          const ratesBlock = !showRates ? null : rates.length > 0 ? (
            <section key="rates">
              <h2 style={{ margin: "0 0 16px", fontWeight: 600 }}>Rates</h2>
              <ul
                className="m-0 list-none overflow-hidden rounded-2xl border p-0"
                style={{ borderColor: "var(--pf-border)" }}
              >
                {rates.map((rate) => (
                  <li
                    key={rate.id}
                    className="flex items-center gap-3 border-t first:border-t-0"
                    style={{
                      borderColor: "var(--pf-border)",
                      padding: "13px 16px",
                    }}
                  >
                    <span
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                      style={{
                        background: "var(--pf-surface)",
                        color: "var(--pf-accent)",
                      }}
                      aria-hidden="true"
                    >
                      <RateGlyph label={rate.label} custom={rate.custom} />
                    </span>
                    <div className="min-w-0 flex-1 wrap-break-word">
                      <div style={rateText}>{rate.label}</div>
                      {rate.note && (
                        <p
                          className="text-xs"
                          style={{
                            color: "var(--pf-muted)",
                            margin: "2px 0 0",
                          }}
                        >
                          {rate.note}
                        </p>
                      )}
                    </div>
                    <div
                      className="shrink-0 whitespace-nowrap tabular-nums"
                      style={{ ...rateText, letterSpacing: "-0.005em" }}
                    >
                      {rate.price}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : isEditor ? (
            <div key="rates" className="rounded-2xl">
              <EmptyPrompt
                icon={CircleDollarSign}
                message="Enter your content rates in your profile."
                cta={
                  editHrefs
                    ? { label: "Add rates", href: editHrefs.profile }
                    : undefined
                }
              />
            </div>
          ) : null;

          // The prototype's ContactSection (studio variant) without the email
          // row: an h2 + muted subtitle above the "Email me" CTA. The address
          // isn't shown or in the markup (EPD-12502); the button loads it.
          const contactBlock = !showContact ? null : hasContact ? (
            <section key="contact">
              <div style={{ margin: "0 0 24px" }}>
                <h2 style={{ margin: 0, fontWeight: 600 }}>Contact</h2>
                <p
                  className="text-[13px]"
                  style={{ color: "var(--pf-muted)", margin: "6px 0 0" }}
                >
                  Let’s work together
                </p>
              </div>
              <button
                type="button"
                onClick={() => void openContactEmail()}
                disabled={openingContact}
                aria-busy={openingContact}
                className="inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-lg px-[22px] py-3 text-[13px] font-semibold transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-wait disabled:opacity-70"
                style={{
                  background: "var(--pf-accent)",
                  color: "var(--pf-accent-text)",
                }}
              >
                Email me<span aria-hidden="true">&nbsp;→</span>
              </button>
            </section>
          ) : isEditor ? (
            <div key="contact" className="rounded-2xl">
              <EmptyPrompt
                icon={Mail}
                title="Let brands reach you"
                message="Add a contact email in the Contact section of the customize panel."
              />
            </div>
          ) : null;

          const isSocialSection = (section: PortfolioSection) =>
            section === "content" || section === "stats";
          const otherBlock = (
            section: PortfolioSection,
          ): ReactElement | null => {
            if (section === "highlights") return highlightsBlock;
            if (section === "rates") return ratesBlock;
            if (section === "contact") return contactBlock;
            return null;
          };

          // Recent Posts directly under the header is often the page's
          // largest paint (EPD-12468): its first images load eagerly then.
          // "Directly under" means no section that shows anything comes
          // before it; a hidden or empty section ahead of it doesn't count.
          const firstShown = sections.find((section) =>
            isSocialSection(section)
              ? socialAccountsOn
              : otherBlock(section) !== null,
          );
          const socialAccountsFirst =
            firstShown !== undefined && isSocialSection(firstShown);
          let socialAccountsBlock: ReactElement | null = null;
          if (socialAccountsOn && shownSocialStats) {
            socialAccountsBlock = (
              <PortfolioSocialAccounts
                key="social-accounts"
                entries={shownSocialStats}
                showContent={showContent}
                showStats={showStats}
                showAudience={showAudience}
                hiddenMetricIds={theme.hiddenMetricIds}
                surface={surface}
                channelsHref={editHrefs?.channels}
                creatorName={data.profile.name}
                prioritizeImages={socialAccountsFirst}
              />
            );
          } else if (
            socialStatsPending &&
            showStats &&
            channels.some((channel) => channel.connected)
          ) {
            // Before the stats arrive, hold the block's place only when it is
            // sure to render (EPD-12469). With stats on, every connected
            // account gets a tab. With stats off, a tab also needs recent posts
            // or audience data, which aren't known until the stats land, so
            // those portfolios keep the old behavior and get no placeholder.
            socialAccountsBlock = (
              <PortfolioSocialAccountsPlaceholder key="social-accounts" />
            );
          }

          // Render body sections in the creator's order (real-time preview of the
          // Sections panel). The social icons live in the fixed header above;
          // content/stats (and the Metrics Shown demographics) all belong to the
          // Social Accounts block, rendered once at whichever comes first.
          let socialAccountsDone = false;
          // Dedupe (in `sections` above): the theme schema permits repeated
          // section names, which would otherwise render a block twice with
          // duplicate sibling keys.
          return sections
            .map((section) => {
              if (isSocialSection(section)) {
                if (socialAccountsDone) return null;
                socialAccountsDone = true;
                return socialAccountsBlock;
              }
              return otherBlock(section);
            })
            .filter((block): block is ReactElement => block !== null);
        })()}
      </div>
    </div>
  );
}
